import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { queryKeys } from '@splitfiesta/core';
import type { UserNetBalanceResponse } from '@splitfiesta/core';
import type {
  BalanceResponseDto,
  SettlementResponseDto,
  SuccessResponseDto,
} from '@splitfiesta/shared';

import { useTypedApi } from '@/hooks/useTypedApi';

// Fetch user balance
export function useUserBalanceQuery(
  userId: string,
  groupId?: string,
  enabled = true
) {
  const api = useTypedApi();

  return useQuery({
    queryKey: queryKeys.userBalance(userId, groupId),
    queryFn: async () => {
      const url = groupId
        ? `/balance/user/${userId}?groupId=${groupId}`
        : `/balance/user/${userId}`;
      const response = await api.get<BalanceResponseDto>(url);
      if ('data' in response && response.data) {
        return response.data as BalanceResponseDto;
      }
      return response as BalanceResponseDto;
    },
    enabled: enabled && !!userId,
  });
}

// Fetch user's net balance across all groups
export function useUserNetBalanceQuery(enabled = true) {
  const api = useTypedApi();

  return useQuery({
    queryKey: queryKeys.userNetBalance(),
    queryFn: async () => {
      const response = await api.balances.getUserNet();
      return response.data as UserNetBalanceResponse;
    },
    enabled,
  });
}

// Fetch group balances
export function useGroupBalancesQuery(groupId: string, enabled = true) {
  const api = useTypedApi();

  return useQuery({
    queryKey: queryKeys.groupBalances(groupId),
    queryFn: async () => {
      const response = await api.get<
        SuccessResponseDto<{
          balances: Array<{
            userId: string;
            userName: string;
            balance: Record<string, number>;
          }>;
        }>
      >(`/balance/group/${groupId}`);
      if ('data' in response && response.data) {
        return response.data;
      }
      // Fallback - return empty balances array
      return {
        balances: [],
      };
    },
    enabled: enabled && !!groupId,
  });
}

// Fetch debt suggestions (simplified debts)
export function useDebtSuggestionsQuery(
  groupId: string,
  enabled = true,
  filterByUser = false
) {
  const api = useTypedApi();

  return useQuery({
    queryKey: queryKeys.simplifiedDebts(groupId, filterByUser),
    queryFn: async () => {
      const url = filterByUser
        ? `/groups/${groupId}/simplified-debts?filterByUser=true`
        : `/groups/${groupId}/simplified-debts`;
      const response = await api.get<
        SuccessResponseDto<{
          groupId: string;
          groupName: string;
          currency: string;
          recommendations: Array<{
            fromUser: {
              id: string;
              name: string;
              profileImage?: string;
            };
            toUser: {
              id: string;
              name: string;
              profileImage?: string;
            };
            amount: number;
            currency: string;
            priority?: number;
          }>;
          pendingSettlements: Array<{
            id: string;
            fromUser: {
              id: string;
              name: string;
              profileImage?: string;
            };
            toUser: {
              id: string;
              name: string;
              profileImage?: string;
            };
            amount: number;
            currency: string;
            status: 'pending';
          }>;
          totalTransactions: number;
          potentialSavings: number;
          lastCalculated: string;
        }>
      >(url);
      if ('data' in response && response.data) {
        return response.data;
      }
      // Fallback - return empty recommendations
      return {
        groupId,
        groupName: '',
        currency: 'USD',
        recommendations: [],
        pendingSettlements: [],
        totalTransactions: 0,
        potentialSavings: 0,
        lastCalculated: new Date().toISOString(),
      };
    },
    enabled: enabled && !!groupId,
  });
}

// Fetch settlements for a group
export function useGroupSettlementsQuery(groupId: string, enabled = true) {
  const api = useTypedApi();

  return useQuery({
    queryKey: queryKeys.settlements.group(groupId),
    queryFn: async () => {
      const response = await api.get<
        SuccessResponseDto<{
          settlements: SettlementResponseDto[];
        }>
      >(`/settlements/group/${groupId}`);
      if ('data' in response && response.data) {
        return response.data;
      }
      return {
        settlements: [] as SettlementResponseDto[],
      };
    },
    enabled: enabled && !!groupId,
  });
}

// Create settlement mutation
export function useCreateSettlementMutation() {
  const api = useTypedApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (data: {
      groupId: string;
      toUserId: string;
      amount: number;
      currency: string;
      paymentMethod: string;
      notes?: string;
    }) => {
      const response = await api.post<SettlementResponseDto>(
        '/settlements',
        data
      );
      if ('data' in response && response.data) {
        return response.data;
      }
      return response;
    },
    onSuccess: (_, variables) => {
      // Invalidate settlements list
      queryClient.invalidateQueries({
        queryKey: queryKeys.settlements.group(variables.groupId),
      });
      // Invalidate balances as they might have changed
      queryClient.invalidateQueries({
        queryKey: queryKeys.groupBalances(variables.groupId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.userNetBalance(),
      });
    },
  });
}

// Mark settlement as paid
export function useCompleteSettlementMutation() {
  const api = useTypedApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      settlementId,
      groupId: _groupId,
    }: {
      settlementId: string;
      groupId: string;
    }) => {
      const response = await api.put<SettlementResponseDto>(
        `/settlements/${settlementId}/complete`
      );
      if ('data' in response && response.data) {
        return response.data;
      }
      return response;
    },
    onSuccess: (_, { groupId }) => {
      // Invalidate settlements
      queryClient.invalidateQueries({
        queryKey: queryKeys.settlements.group(groupId),
      });
      // Invalidate balances
      queryClient.invalidateQueries({
        queryKey: queryKeys.groupBalances(groupId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.userNetBalance(),
      });
    },
  });
}
